import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dataManager from './dataManager';
import { setAuthError } from '../store/reducers/stateReducer';

/**
 * React хук для управления данными приложения
 * @param {Object} auth - Объект авторизации
 * @returns {Object} Объект с методами для управления данными
 */
export const useDataManager = (auth) => {
  const dispatch = useDispatch();
  const status = useSelector((state) => state.state.status);
  const hasInitialDataLoaded = useRef(false);
  const user = useSelector(state => state.state.user)

  // Инициализация DataManager
  useEffect(() => {
    if (auth && dispatch) {
      dataManager.initialize(dispatch, auth);
    }
  }, [auth, dispatch]);

  // Очистка при размонтировании компонента
  useEffect(() => {
    return () => {
      dataManager.stopAllIntervals();
    };
  }, []);

  // Обработка авторизации пользователя и загрузка данных
  useEffect(() => {
    if (auth?.isAuthenticated && auth?.user) {
      // Проверяем наличие пользователя в группе Gis Users
      const userGroups = auth.user.profile?.groups || [];
      const hasGisUsersAccess = userGroups.includes('Gis Users');
      
      if (!hasGisUsersAccess) {
        console.log('❌ Пользователь не имеет доступа к группе Gis Users');
        console.log('👥 Группы пользователя:', userGroups);
        // Устанавливаем флаг ошибки авторизации
        dispatch(setAuthError(true));
        return;
      }

      // Сначала загружаем пользователя
      if (!user || user.id === 0) {
        const tokenInfo = {
          access_token: auth.user.access_token,
          expiryTime: auth.user.expires_at ? new Date(auth.user.expires_at * 1000) : null
        };

        if (tokenInfo.access_token) {
          console.log('👤 Загрузка пользователя...');
          dataManager.loadUser(tokenInfo.access_token, auth.user.profile.preferred_username);
        }
      }


    } else if (!auth?.isAuthenticated) {
      // Сброс флагов при выходе пользователя
      console.log('🔄 Сброс флагов загрузки данных - пользователь не авторизован');
      hasInitialDataLoaded.current = false;
      dataManager.reset();
      // Сбрасываем флаг ошибки авторизации
      dispatch(setAuthError(false));
    }
  }, [auth?.isAuthenticated, auth?.user]);

  useEffect(() => {
    // Затем загружаем первоначальные данные (только после загрузки пользователя)
    if (user && user.id !== 0 && !hasInitialDataLoaded.current) {
      console.log('🔄 Первоначальная загрузка данных при авторизации');
      console.log('👤 Пользователь авторизован:', auth.user?.profile);
      
      hasInitialDataLoaded.current = true;
      dataManager.loadInitialData();
    }
  }, [user])

  // Интервал для регулярного обновления данных
  useEffect(() => {
    if (!user || user.id === 0 || !auth?.isAuthenticated) {
      dataManager.stopRegularRefresh();
      return;
    }

    console.log('🔄 Запуск регулярных обновлений данных');
    dataManager.startRegularRefresh();

    return () => {
      dataManager.stopRegularRefresh();
    };
  }, [user, auth?.isAuthenticated]);

  // Интервал для проверки статуса при ошибках
  useEffect(() => {
    if (status !== 'failed' || !auth?.isAuthenticated) {
      dataManager.stopErrorStatusCheck();
      return;
    }

    console.log('🔄 Запуск проверки статуса при ошибках');
    dataManager.startErrorStatusCheck();

    return () => {
      dataManager.stopErrorStatusCheck();
    };
  }, [status, auth?.isAuthenticated]);

  // Возвращаем методы для внешнего использования
  return {
    loadInitialData: () => dataManager.loadInitialData(),
    refreshData: () => dataManager.refreshData(),
    checkStatusOnError: () => dataManager.checkStatusOnError(),
    startRegularRefresh: (intervalMs) => dataManager.startRegularRefresh(intervalMs),
    stopRegularRefresh: () => dataManager.stopRegularRefresh(),
    startErrorStatusCheck: (intervalMs) => dataManager.startErrorStatusCheck(intervalMs),
    stopErrorStatusCheck: () => dataManager.stopErrorStatusCheck(),
    stopAllIntervals: () => dataManager.stopAllIntervals(),
    getIntervalsInfo: () => dataManager.getIntervalsInfo(),
    reset: () => {
      hasInitialDataLoaded.current = false;
      dataManager.reset();
    }
  };
}; 